import { createApolloClient } from "../../providers/ApolloClientFactory";
import { GET_DEVICES, GET_DEVICES_COUNT, GET_VENDOR_NAMES } from "./queries";
import { Device } from "../../types/device";
import { Link, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment/moment";
import { useOrganizationStore } from "../../store/organization";
import { useEventStore } from "../../store/event";
import { usePermissions } from "../auth/api";
import { Input } from "../../components/inputs/Input";
import { PrimaryButton, SecondaryButton } from "../../components/Button";

const client = createApolloClient();

const PAGE_SIZE = 100;

// Loader kept as a no-op so the existing route entry doesn't error. All data
// fetching happens in the component so it can react to header (org/event)
// changes, pagination, search, and sort without going through the router.
export const devicesListLoader = async () => null;

// This page deliberately shows ONLY what the POS heartbeat actually writes
// (deviceHeartbeat.js in posapp): updated_at, active_event_id,
// active_vendor_id, session_order_count, reported_android_id — plus the
// minimum identity needed to recognize a unit (MAC / license plate). The rest
// of the devices row is Quickbase-imported asset data that routinely
// contradicts the live telemetry; it lives on the device detail page, not here.

interface HealthVerdict {
  label: string;
  color: string;
  pillBg: string;
  pillText: string;
}

const verdictFor = (heartbeatAt: string | null | undefined): HealthVerdict => {
  if (!heartbeatAt) {
    return { label: "Unknown", color: "text-gray-400", pillBg: "bg-gray-100", pillText: "text-gray-600" };
  }
  const minutes = moment().diff(moment(heartbeatAt), "minutes", true);
  if (minutes < 5) return { label: "Online", color: "text-green-700", pillBg: "bg-green-50", pillText: "text-green-700" };
  if (minutes < 60) return { label: "Idle", color: "text-gray-700", pillBg: "bg-gray-100", pillText: "text-gray-700" };
  if (minutes < 60 * 24) return { label: "Stale", color: "text-amber-700", pillBg: "bg-amber-50", pillText: "text-amber-700" };
  return { label: "Offline", color: "text-red-700", pillBg: "bg-red-50", pillText: "text-red-700" };
};

const fromNow = (iso: string | null | undefined) => (iso ? moment(iso).fromNow() : "—");

const Stat = ({ label, value, color }: { label: string; value: string; color?: string }) => (
  <div className="flex flex-col">
    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
    <span className={`text-sm font-semibold tabular-nums ${color ?? "text-gray-900"}`}>{value}</span>
  </div>
);

type SortDir = "desc" | "asc";

export const DeviceList = () => {
  const { editCreatePermission } = usePermissions("devices");
  const navigate = useNavigate();
  const headerOrgId = useOrganizationStore((s) => s.organizationId);
  const headerEventId = useEventStore((s) => s.eventId);
  const allEvents = useEventStore((s) => s.allEvents);
  const organizations = useOrganizationStore((s) => s.organizations);

  const headerEventName = useMemo(() => {
    if (!headerEventId) return null;
    const evt = (allEvents ?? []).find((e: any) => Number(e?.id) === Number(headerEventId));
    return evt?.name ?? null;
  }, [allEvents, headerEventId]);

  const headerOrgName = useMemo(() => {
    if (!headerOrgId) return null;
    const org = (organizations ?? []).find((o: any) => Number(o?.id) === Number(headerOrgId));
    return org?.name ?? null;
  }, [organizations, headerOrgId]);

  // Resolve a POS-reported active_event_id to an event name for display. No FK
  // relationship exists on the column, so we map client-side off the header's
  // already-loaded event list; fall back to the raw id when it's not found.
  const eventNameById = useMemo(() => {
    const map = new Map<number, string>();
    (allEvents ?? []).forEach((e: any) => {
      if (e?.id != null && e?.name) map.set(Number(e.id), e.name);
    });
    return map;
  }, [allEvents]);

  // Vendor id → name, fetched once, to label the vendor groups and the CSV.
  const [vendorNameById, setVendorNameById] = useState<Map<number, string>>(new Map());

  const [devices, setDevices] = useState<Device[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // The one filter this page needs: hide rows that have never heartbeated.
  const [liveOnly, setLiveOnly] = useState(false);

  // Debounce search → triggers refetch via the main effect.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchInput.trim()), 250);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Reset to page 0 whenever scope / search / sort changes.
  useEffect(() => {
    setPage(0);
  }, [headerOrgId, headerEventId, debouncedSearch, sortDir, liveOnly]);

  // Load vendor id → name once, for the vendor group headers and CSV.
  useEffect(() => {
    let cancelled = false;
    client.query({ query: GET_VENDOR_NAMES, fetchPolicy: "network-only" })
      .then(({ data }) => {
        if (cancelled) return;
        const map = new Map<number, string>();
        (data?.vendors ?? []).forEach((v: any) => {
          if (v?.id != null && v?.name) map.set(Number(v.id), v.name);
        });
        setVendorNameById(map);
      })
      .catch(() => { /* non-fatal — falls back to "Vendor #id" */ });
    return () => { cancelled = true; };
  }, []);

  const buildWhere = useCallback(() => {
    const ands: any[] = [];

    if (debouncedSearch) {
      const q = `%${debouncedSearch}%`;
      ands.push({
        _or: [
          { mac_address: { _ilike: q } },
          { license_plate: { _ilike: q } },
          { android_device_id: { _ilike: q } },
          { reported_android_id: { _ilike: q } },
        ],
      });
    }

    // Scope by LIVE telemetry only: the POS-reported active_event_id. The old
    // fallbacks (Quickbase deployed_event_name / current_possession strings)
    // matched asset records that regularly contradict where devices really are.
    if (headerEventId) {
      ands.push({ active_event_id: { _eq: Number(headerEventId) } });
    } else if (headerOrgId) {
      const orgEventIds = (allEvents ?? [])
        .map((e: any) => Number(e?.id))
        .filter((n: number) => Number.isFinite(n));
      if (orgEventIds.length > 0) {
        ands.push({ active_event_id: { _in: orgEventIds } });
      }
    }

    if (liveOnly) {
      ands.push({ active_event_id: { _is_null: false } });
    }

    if (ands.length === 0) return undefined;
    if (ands.length === 1) return ands[0];
    return { _and: ands };
  }, [debouncedSearch, headerEventId, headerOrgId, allEvents, liveOnly]);

  const fetchDevices = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const where = buildWhere();
      const { data } = await client.query({
        query: GET_DEVICES,
        variables: {
          where,
          orderBy: [{ updated_at: sortDir }],
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
        },
        fetchPolicy: "network-only",
      });
      const rows = (data?.devices ?? []) as Device[];
      setDevices(rows);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load devices");
      setDevices([]);
    } finally {
      setIsLoading(false);
    }
  }, [buildWhere, page, sortDir]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  // Count is only meaningful per where-clause — refetch only when filters
  // change, not on every page step. The aggregate is the slowest part of the
  // original combined query when the result set is large.
  useEffect(() => {
    const where = buildWhere();
    let cancelled = false;
    client
      .query({ query: GET_DEVICES_COUNT, variables: { where }, fetchPolicy: "network-only" })
      .then(({ data }) => {
        if (cancelled) return;
        setTotalCount(data?.devices_aggregate?.aggregate?.count ?? 0);
      })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, [buildWhere]);

  const rows = useMemo(
    () =>
      devices.map((d) => ({
        device: d,
        verdict: verdictFor(d.updated_at),
      })),
    [devices]
  );

  const vendorLabelFor = useCallback(
    (vendorId: number | null | undefined) =>
      vendorId != null ? (vendorNameById.get(Number(vendorId)) ?? `Vendor #${vendorId}`) : null,
    [vendorNameById]
  );

  // Group the loaded rows by active vendor for display. Grouping is over the
  // current page only, so scope to an event via the header to keep a vendor's
  // devices together on one page. Named vendors sort alphabetically; devices with
  // no active session ("No active vendor") sort last.
  const groupedRows = useMemo(() => {
    const groups = new Map<
      string,
      { key: string; label: string; vendorId: number | null; items: typeof rows }
    >();
    for (const r of rows) {
      const vid = r.device.active_vendor_id ?? null;
      const key = vid != null ? `v:${vid}` : "none";
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          label: vendorLabelFor(vid) ?? "No active vendor",
          vendorId: vid,
          items: [],
        });
      }
      groups.get(key)!.items.push(r);
    }
    return Array.from(groups.values()).sort((a, b) => {
      if (a.vendorId == null) return 1;
      if (b.vendorId == null) return -1;
      return a.label.localeCompare(b.label);
    });
  }, [rows, vendorLabelFor]);

  const summary = useMemo(() => {
    const counts = { total: totalCount, online: 0, idle: 0, stale: 0, offline: 0, unknown: 0 };
    for (const r of rows) {
      const l = r.verdict.label;
      if (l === "Online") counts.online++;
      else if (l === "Idle") counts.idle++;
      else if (l === "Stale") counts.stale++;
      else if (l === "Offline") counts.offline++;
      else counts.unknown++;
    }
    return counts;
  }, [rows, totalCount]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const fromN = totalCount === 0 ? 0 : page * PAGE_SIZE + 1;
  const toN = Math.min(totalCount, (page + 1) * PAGE_SIZE);

  const handleExportCSV = () => {
    const header = [
      "Mac Address", "License Plate",
      "Registered Android ID", "Reported Android ID",
      "Last Seen", "Status",
      "Active Event", "Active Vendor", "Session Orders",
    ];
    const lines = [header.join(",")].concat(
      rows.map((r) =>
        [
          r.device.mac_address ?? "",
          r.device.license_plate ?? "",
          r.device.android_device_id ?? "",
          r.device.reported_android_id ?? "",
          r.device.updated_at ?? "",
          r.verdict.label,
          r.device.active_event_id != null
            ? (eventNameById.get(Number(r.device.active_event_id)) ?? `Event #${r.device.active_event_id}`)
            : "",
          vendorLabelFor(r.device.active_vendor_id) ?? "",
          r.device.session_order_count != null ? String(r.device.session_order_count) : "",
        ]
          .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
          .join(",")
      )
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Devices_${moment().format("YYYYMMDD_HHmmss")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const scopeLabel = headerEventId
    ? `Devices live at ${headerEventName ?? "the selected event"}`
    : headerOrgId
      ? `Devices live at ${headerOrgName ?? "the selected organization"}'s events`
      : "All devices";

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      {/* Header card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-4 py-3 mb-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Devices</h1>
            <div className="text-xs text-gray-500">{scopeLabel}</div>
          </div>
          <div className="flex items-center gap-2">
            <SecondaryButton onClick={() => navigate(`/devices/app-updates`)}>App Updates</SecondaryButton>
            <SecondaryButton onClick={handleExportCSV}>Export CSV</SecondaryButton>
            {editCreatePermission && (
              <PrimaryButton onClick={() => navigate(`/devices/create`)}>Create</PrimaryButton>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div className="md:col-span-2">
            <Input
              name="search"
              placeholder="Search MAC, license plate, Android ID"
              value={searchInput}
              onChange={(v) => setSearchInput(typeof v === "string" ? v : "")}
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 cursor-pointer mr-2">
              <input
                type="checkbox"
                checked={liveOnly}
                onChange={() => setLiveOnly((v) => !v)}
                className="h-3.5 w-3.5 rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              Live only
            </label>
            <span className="text-xs text-gray-500">{summary.total} devices</span>
            {summary.online > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-700">{summary.online} online</span>
            )}
            {summary.stale > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">{summary.stale} stale</span>
            )}
            {summary.offline > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700">{summary.offline} offline</span>
            )}
          </div>
        </div>
      </div>

      {/* Sort + pagination controls */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-3 px-1">
        <button
          type="button"
          onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))}
          className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-gray-900 transition-colors"
        >
          Sort: Last seen
          <span className="text-gray-400">{sortDir === "desc" ? "↓ newest" : "↑ oldest"}</span>
        </button>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500 tabular-nums">
            {totalCount === 0 ? "0 of 0" : `${fromN}–${toN} of ${totalCount}`}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0 || isLoading}
            className="px-3 py-1 text-xs font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Prev
          </button>
          <span className="text-xs text-gray-500 tabular-nums">{page + 1} / {totalPages}</span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1 || isLoading}
            className="px-3 py-1 text-xs font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">{error}</div>
      )}

      {isLoading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-20 text-gray-400">No devices match the current filters.</div>
      ) : (
        <div className="space-y-6">
          {groupedRows.map((group) => (
            <div key={group.key} className="space-y-3">
              <div className="flex items-baseline gap-2 px-1">
                <h2 className="text-sm font-bold text-gray-800">{group.label}</h2>
                <span className="text-xs text-gray-400 tabular-nums">{group.items.length}</span>
              </div>
              {group.items.map(({ device, verdict }) => {
                // POS live session (from the heartbeat).
                const activeEventName =
                  device.active_event_id != null
                    ? (eventNameById.get(Number(device.active_event_id)) ?? `Event #${device.active_event_id}`)
                    : null;
                // The device reports its own ANDROID_ID; flag when it doesn't match
                // the Quickbase-registered android_device_id (likely a bad QB record).
                const idMismatch =
                  !!device.reported_android_id &&
                  !!device.android_device_id &&
                  device.reported_android_id !== device.android_device_id;

                return (
                  <Link
                    key={device.id}
                    to={`/devices/${device.id}`}
                    className="block bg-white border border-gray-200 rounded-xl shadow-sm hover:border-red-500 transition-colors"
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 px-4 py-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-gray-900 text-base">
                            {device.mac_address || `Device #${device.id}`}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${verdict.pillBg} ${verdict.pillText}`}>
                            {verdict.label}
                          </span>
                          {device.license_plate && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                              #{device.license_plate}
                            </span>
                          )}
                          {idMismatch && (
                            <span
                              className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700"
                              title={`Reported ${device.reported_android_id} ≠ registered ${device.android_device_id}`}
                            >
                              ID mismatch
                            </span>
                          )}
                        </div>
                        {activeEventName ? (
                          <div className="text-xs text-green-700 mt-0.5 truncate">
                            <span className="font-semibold">● Live:</span> {activeEventName}
                          </div>
                        ) : (
                          <div className="text-xs text-gray-400 mt-0.5">No active session</div>
                        )}
                      </div>

                      <div className="flex items-center gap-6 flex-shrink-0">
                        <Stat label="Last seen" value={fromNow(device.updated_at)} color={verdict.color} />
                        <Stat
                          label="Session orders"
                          value={device.session_order_count != null ? String(device.session_order_count) : "—"}
                        />
                        <div className="flex flex-col items-end">
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Actions</span>
                          {editCreatePermission ? (
                            <span
                              role="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                navigate(`/devices/${device.id}/edit`);
                              }}
                              className="text-sm font-semibold text-red-700 hover:text-red-800 cursor-pointer"
                            >
                              Edit
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400">—</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
